/**
 * The competitive half of the cockpit: today's activity against my own last week, the board
 * (the top three and the places either side of me), and the floor's sales as they land.
 * The quality counterweight sits beside the volume: policies still on the books at 90 days.
 */
import type { Cockpit } from "@tlcrm/shared";

import { Avatar, KpiTile } from "../../components/kit";
import { percent } from "../../lib/chart";
import { boardRows } from "../../lib/cockpit";
import { ago, hoursMinutes, money } from "../../lib/format";

/** "Up from 26% a day" against the six days before today. */
function versus(
  today: number,
  before: number[],
  format: (n: number) => string,
  lowerIsBetter = false,
): string {
  const days = before.filter((n) => n > 0);
  if (!days.length) return "First day on record";
  const avg = days.reduce((a, b) => a + b, 0) / days.length;
  if (Math.abs(today - avg) < avg * 0.05) return `Level with your ${format(avg)} a day`;
  const better = lowerIsBetter ? today < avg : today > avg;
  return `${better ? "Better than" : lowerIsBetter ? "Slower than" : "Below"} your ${format(avg)} a day`;
}

export function ActivityRow({ cockpit }: { cockpit: Cockpit }) {
  const a = cockpit.activity;
  const prior = (xs: number[]) => xs.slice(0, -1);
  return (
    <div className="kpi-row-grid">
      <KpiTile
        label="Calls today"
        value={a.calls}
        spark={a.spark.calls}
        sub={
          <>
            {a.answered} answered · {a.outbound} outbound ·{" "}
            {versus(a.calls, prior(a.spark.calls), (n) => n.toFixed(0))}
          </>
        }
      />
      <KpiTile
        label="Talk time"
        value={hoursMinutes(a.talkSeconds)}
        spark={a.spark.talkMinutes}
        sub={versus(a.talkSeconds / 60, prior(a.spark.talkMinutes), (n) => hoursMinutes(n * 60))}
      />
      <KpiTile
        label="Contact rate"
        value={a.contactRate === null ? "–" : percent(a.contactRate)}
        spark={a.spark.contactRate}
        sub={
          a.contactRate === null
            ? "No outbound dials yet today"
            : versus(a.contactRate, prior(a.spark.contactRate), percent)
        }
      />
      <KpiTile
        label="Speed to lead"
        value={
          a.speedToLeadSeconds === null
            ? "–"
            : hoursMinutes(a.speedToLeadSeconds).replace(/^0m$/, "<1m")
        }
        spark={a.spark.speedMinutes}
        sub={
          a.speedToLeadSeconds === null
            ? "No new leads reached yet today"
            : versus(
                a.speedToLeadSeconds / 60,
                prior(a.spark.speedMinutes),
                (n) => hoursMinutes(n * 60),
                true,
              )
        }
      />
      <KpiTile
        label="Still on the books at 90 days"
        value={cockpit.me.kept90 === null ? "–" : percent(cockpit.me.kept90)}
        sub={
          cockpit.me.kept90 === null
            ? "Shows once a sale is 90 days old"
            : `Cancellation rate ${percent(cockpit.me.cancellationRate)} all time`
        }
      />
    </div>
  );
}

function Moved({ n }: { n: number }) {
  if (n > 0)
    return (
      <span className="moved is-up" title={`Up ${n} since yesterday`}>
        ▲{n}
      </span>
    );
  if (n < 0)
    return (
      <span className="moved" title={`Down ${-n} since yesterday`}>
        ▼{-n}
      </span>
    );
  return (
    <span className="moved" title="No change since yesterday">
      –
    </span>
  );
}

export function Leaderboard({
  cockpit,
  meId,
  now,
}: {
  cockpit: Cockpit;
  meId: string;
  now: number;
}) {
  const rows = boardRows(cockpit.leaders, meId);
  return (
    <section className="card board">
      <div className="card-head">
        <h3>Leaderboard</h3>
        <span className="card-sub">Closed Won premium this week</span>
      </div>
      {rows.length === 0 ? (
        <div className="card-empty">
          <b>The board is open.</b> The first sale of the week takes #1.
        </div>
      ) : (
        <ol className="board-list">
          {rows.map((r, i) =>
            r === "gap" ? (
              <li key={`gap${i}`} className="board-gap" aria-hidden="true">
                ···
              </li>
            ) : (
              <li
                key={r.owner.id}
                className={`board-row${r.owner.id === meId ? " is-me" : ""}${r.rank <= 3 ? " is-top" : ""}`}
              >
                <span className="board-rank mono">{r.rank}</span>
                <Avatar name={r.owner.fullName} you={r.owner.id === meId} size="sm" />
                <span className="board-name">
                  <b>{r.owner.id === meId ? "You" : r.owner.fullName}</b>
                  <span>
                    {r.count} {r.count === 1 ? "policy" : "policies"}
                    {r.lastSaleAt && ` · last sale ${ago(r.lastSaleAt, now)}`}
                  </span>
                </span>
                <span className="board-premium mono">{money(r.premium)}</span>
                <Moved n={r.moved} />
              </li>
            ),
          )}
        </ol>
      )}
      {cockpit.me.rank && cockpit.me.gapToNext !== null && (
        <div className="card-foot board-foot">
          <span>
            <b className="mono">{money(cockpit.me.gapToNext)}</b> more moves you to #
            {cockpit.me.rank - 1}
          </span>
        </div>
      )}
    </section>
  );
}

export function FloorWins({
  wins,
  meId,
  now,
}: {
  wins: Cockpit["wins"];
  meId: string;
  now: number;
}) {
  return (
    <section className="card wins">
      <div className="card-head">
        <h3>Floor wins</h3>
        <span className="card-sub">Every sale this week, as it lands</span>
      </div>
      {wins.length === 0 ? (
        <div className="card-empty">
          <b>No sales yet this week.</b> Be the first name here.
        </div>
      ) : (
        <ul className="wins-list">
          {wins.slice(0, 6).map((w, i) => (
            <li key={`${w.owner.id}${w.at}${i}`} className="wins-row">
              <Avatar name={w.owner.fullName} you={w.owner.id === meId} size="sm" />
              <span className="wins-what">
                <b>{w.owner.id === meId ? "You" : w.owner.fullName.split(" ")[0]}</b> sold{" "}
                {[w.carrier, w.product].filter(Boolean).join(" ") || "a policy"}
                <span>{ago(w.at, now)}</span>
              </span>
              <span className="mono wins-premium">{money(w.premium)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
